import React, { useState } from 'react';
import { Package, Search, Phone, Mail, AlertCircle, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { Helmet } from 'react-helmet-async';

const TrackShipmentPage = () => {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('');
  const [showOthersMessage, setShowOthersMessage] = useState(false);
  const { toast } = useToast();

  const carriers = [
    { value: '', label: 'Select Carrier / Shipping Provider' },
    { value: 'DHL', label: 'DHL' },
    { value: 'FEDEX', label: 'FEDEX' },
    { value: 'UPS', label: 'UPS' },
    { value: 'GLS', label: 'GLS' },
    { value: 'FFCL', label: 'FFCL' },
    { value: 'DTDC', label: 'DTDC' },
    { value: 'OTHERS', label: 'Others' }
  ];

  const getTrackingUrl = (carrier, trackingNum) => {
    const urls = {
      'DHL': `https://www.dhl.com/ca-en/home/tracking.html?tracking-id=${encodeURIComponent(trackingNum)}`,
      'FEDEX': `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(trackingNum)}`,
      'UPS': `https://www.ups.com/track?loc=en_CA&tracknum=${encodeURIComponent(trackingNum)}`,
      'GLS': 'https://gls-group.com/CA/en/send-and-receive/track-a-shipment/',
      'FFCL': 'https://www.firstflightcanada.com/tracking',
      'DTDC': 'https://canada.dtdc.com/tracking'
    };
    return urls[carrier] || null;
  };

  const handleTrack = (e) => {
    e.preventDefault();
    
    // Validation
    if (!trackingNumber.trim()) {
      toast({
        title: "Tracking Number Required",
        description: "Please enter your tracking number to continue.",
        variant: "destructive"
      });
      return;
    }

    if (!carrier) {
      toast({
        title: "Carrier Selection Required",
        description: "Please select your shipping provider to track your shipment.",
        variant: "destructive"
      });
      return;
    }

    // Handle "Others" option
    if (carrier === 'OTHERS') {
      setShowOthersMessage(true);
      toast({
        title: "Manual Tracking Support",
        description: "Please contact our support team for assistance with tracking.",
      });
      return;
    }

    // Open tracking in new tab
    const trackingUrl = getTrackingUrl(carrier, trackingNumber);
    if (trackingUrl) {
      window.open(trackingUrl, '_blank', 'noopener,noreferrer');
      toast({
        title: "Opening Tracking Page",
        description: `Redirecting to ${carrier} tracking in a new tab...`,
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Track Shipment | Shippzy</title>
        <meta name="description" content="Track your Shippzy shipment by entering your tracking number and selecting the shipping provider. Access official DHL, FedEx, UPS, GLS, FFCL, and DTDC tracking pages." />
        <link rel="canonical" href="https://www.shippzy.ca/track-shipment" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="inline-block bg-gradient-to-r from-red-600 to-yellow-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
              📦 Real-Time Tracking
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Track Your Shipment
            </h1>
            <p className="text-xl text-gray-600">
              Enter your tracking number and select the shipping provider to continue tracking your shipment
            </p>
          </div>

          {/* Tracking Form Card */}
          <Card className="shadow-xl mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Search className="w-6 h-6 text-red-600" />
                Track Your Package
              </CardTitle>
              <CardDescription>
                Your tracking number can be found in your shipping confirmation email
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleTrack} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tracking Number *
                  </label>
                  <Input
                    type="text"
                    placeholder="e.g., DHL123456789, 1Z999AA10123456784, or SHPZ-2024-001"
                    value={trackingNumber}
                    onChange={(e) => {
                      setTrackingNumber(e.target.value);
                      setShowOthersMessage(false);
                    }}
                    className="text-lg h-14"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Carrier / Shipping Provider *
                  </label>
                  <select
                    value={carrier}
                    onChange={(e) => {
                      setCarrier(e.target.value);
                      setShowOthersMessage(false);
                    }}
                    className="w-full h-14 px-4 text-lg border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
                    required
                  >
                    {carriers.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white h-14 text-lg font-semibold"
                >
                  <Search className="w-5 h-5 mr-2" />
                  Track Shipment
                </Button>
              </form>

              {/* Others Message */}
              {showOthersMessage && (
                <Card className="mt-6 bg-yellow-50 border-2 border-yellow-400">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">Manual Tracking Support</h3>
                        <p className="text-gray-700 mb-4">
                          We could not identify your carrier from the available list. Please email your tracking number or AWB to our support team and we will assist you with manual tracking support.
                        </p>
                        <div className="space-y-3">
                          <a 
                            href="mailto:ship@shippzy.ca?subject=Tracking Support Request&body=Tracking Number: "
                            className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 font-semibold"
                          >
                            <Mail className="w-4 h-4" />
                            ship@shippzy.ca
                          </a>
                          <div>
                            <Button
                              className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white"
                              onClick={() => window.location.href = '/contact'}
                            >
                              Contact Shippzy Support
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Support Note */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-600 text-center">
                  <strong>Not sure which provider handled your shipment?</strong>
                  <br />
                  Contact Shippzy with your AWB or tracking number.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Carrier Info Section */}
          <Card className="shadow-lg mb-8">
            <CardHeader>
              <CardTitle>Supported Carriers</CardTitle>
              <CardDescription>We support tracking for all major international shipping carriers</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {['DHL', 'FEDEX', 'UPS', 'GLS', 'FFCL', 'DTDC'].map((c) => (
                  <div key={c} className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <Package className="w-8 h-8 text-red-600 mx-auto mb-2" />
                    <p className="font-semibold text-gray-900">{c}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 bg-gradient-to-r from-red-50 to-yellow-50 rounded-lg">
                <p className="text-sm text-gray-700 text-center">
                  <ExternalLink className="w-4 h-4 inline mr-1" />
                  Tracking opens in a new tab on the official carrier website for your security and convenience.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Help Section */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Need Help with Tracking?</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-gray-600">
                  If you can't find your tracking number or need assistance tracking your shipment, our team is here to help.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <a href="tel:+14376160758" className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <Phone className="w-5 h-5 text-red-600" />
                    <div>
                      <p className="text-sm text-gray-600">Call Us</p>
                      <p className="font-semibold text-gray-900">+1 (437) 616-0758</p>
                    </div>
                  </a>
                  <a href="mailto:ship@shippzy.ca" className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <Mail className="w-5 h-5 text-red-600" />
                    <div>
                      <p className="text-sm text-gray-600">Email Us</p>
                      <p className="font-semibold text-gray-900">ship@shippzy.ca</p>
                    </div>
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Back to Home */}
          <div className="text-center mt-8">
            <Button
              variant="outline"
              onClick={() => window.location.href = '/'}
              className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default TrackShipmentPage;
